<template>
    <div class="stat-card">
        <div class="stat-card-inner">
            <div class="d-flex justify-content-between align-items-start">
                <p class="fw-normal small text-muted">{{ title }}</p>
                <div class="m-0" :class="iconBgClass">
                    <i :class="[icon]"></i>
                </div>
            </div>
            <template v-if="loading">
                <div class="stat-value-row pt-2">
                    <div class="shimmer shimmer-value" />
                </div>
                <div class="stat-footer">
                    <div class="shimmer shimmer-pill" />
                </div>
            </template>
            <template v-else>
                <div class="stat-value-row pt-2">
                    <dt ref="counter" class="stat-value fw-semibold" />
                </div>
                <div class="stat-footer" v-if="growth !== null && growth !== undefined">
                    <span class="growth-pill" :class="growthClass">
                        <i :class="growth >= 0 ? 'fa fa-arrow-up' : 'fa fa-arrow-down'" class="growth-icon"></i>
                        {{ growth >= 0 ? '+' : '' }}{{ growth?.toFixed(1) }}%
                    </span>
                    <span class="stat-compare-label">vs last period</span>
                </div>
                <!-- <div class="stat-footer" v-else>
                    <span class="stat-compare-label text-muted">All time</span>
                </div> -->
            </template>
        </div>
    </div>
</template>

<script lang="ts">
import { CountUp, type CountUpOptions } from 'countup.js'
import { merge } from 'lodash'
import { Component, Prop, Ref, Vue, Watch, toNative } from 'vue-facing-decorator'

@Component
class Counter extends Vue {
    counter!: CountUp

    @Ref('counter')
    readonly view!: HTMLElement

    @Prop({ type: String })
    readonly title!: string

    @Prop({ type: String, default: 'text-primary' })
    readonly color!: string

    @Prop({ type: Number, default: 0 })
    readonly value!: number

    @Prop({ type: String, default: 'fa fa-arrow-up' })
    readonly icon!: string

    @Prop({ type: Object, default: () => ({}) })
    readonly options!: CountUpOptions

    @Prop({ type: Number, default: null })
    readonly growth!: number | null

    @Prop({ type: Boolean, default: false })
    readonly loading!: boolean

    @Prop({ type: Object, default: null })
    readonly moneyOptions!: { prefix?: string; decimalPlaces?: number } | null

    get growthClass(): string {
        if (this.growth == null) return 'growth-neutral'
        return this.growth > 0 ? 'growth-positive' : this.growth < 0 ? 'growth-negative' : 'growth-neutral'
    }

    get iconBgClass(): string {
        const map: Record<string, string> = {
            'text-primary': 'icon-bg-primary',
            'text-success': 'icon-bg-success',
            'text-info':    'icon-bg-info',
            'text-warning': 'icon-bg-warning',
            'text-danger':  'icon-bg-danger',
        }
        return map[this.color] ?? 'icon-bg-primary'
    }

    @Watch('value')
    onValueChange(val: number) {
        this.counter?.update(val)
    }

    @Watch('loading')
    onLoadingChange(val: boolean) {
        if (!val) {
            this.$nextTick(() => this.initCounter())
        }
    }

    initCounter() {
        if (!this.view) return
        const opts: CountUpOptions = merge({
            decimalPlaces: this.moneyOptions?.decimalPlaces ?? 0,
            prefix: this.moneyOptions?.prefix ?? '',
        }, this.options)
        this.counter = new CountUp(this.view, this.value, opts)
        this.counter.start()
    }

    mounted() {
        if (!this.loading) this.initCounter()
    }
}

export default toNative(Counter)
</script>

<style lang="scss" scoped>
.stat-card {
    background: #fff;
    height: 100%;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
    transition: box-shadow 0.2s, transform 0.15s;
    overflow: hidden;

    &:hover {
        box-shadow: 0 6px 18px rgba(0,0,0,0.09);
        transform: translateY(-1px);
    }
}

.stat-card-inner {
    padding: 1.25rem 1.4rem 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
}

.stat-top {
    width: 100%;
}

.stat-label {
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: #9ca3af;
    margin: 0;
    line-height: 1.3;
}

.stat-value {
    font-size: 2rem;
    font-weight: 300;
    color: #111827;
    letter-spacing: -0.5px;
    line-height: 1.15;
    display: block;
}

.stat-value-row {
    margin-top: 0.1rem;
}

/* Icon bubble */
.stat-icon-wrap {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 0.85rem;

    &.icon-bg-primary { background: #f0f4e6; color: #3D5A00; }
    &.icon-bg-success { background: #f0f4e6; color: #4a6b0f; }
    &.icon-bg-info    { background: #ecf5f0; color: #2d6b48; }
    &.icon-bg-warning { background: #fdf6ec; color: #b8860b; }
    &.icon-bg-danger  { background: #fff1f2; color: #dc2626; }
}

/* Footer */
.stat-footer {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    margin-top: 0.25rem;
}

.stat-compare-label {
    font-size: 0.72rem;
    color: #9ca3af;
}

/* Growth pill */
.growth-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
    font-size: 0.72rem;
    font-weight: 600;
    padding: 0.15rem 0.45rem;
    border-radius: 20px;

    &.growth-positive {
        color: #15803d;
        background: #dcfce7;
    }
    &.growth-negative {
        color: #b91c1c;
        background: #fee2e2;
    }
    &.growth-neutral {
        color: #6b7280;
        background: #f3f4f6;
    }
}

.growth-icon {
    font-size: 0.6rem;
}

@keyframes shimmer {
    0%   { background-position: -400px 0; }
    100% { background-position:  400px 0; }
}

.shimmer {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 400px 100%;
    animation: shimmer 1.4s ease-in-out infinite;
    border-radius: 6px;
}

.shimmer-value {
    height: 2.2rem;
    width: 60%;
}

.shimmer-pill {
    height: 1rem;
    width: 40%;
}
</style>